import React, { PureComponent } from "react";
import { Box } from "@chakra-ui/react";
import time from "src/utils/time";
import { withTranslation } from "react-i18next";
import { compose } from "recompose";
import MonitoringInfo from "./MonitoringInfo";
import Line from "src/modules/activity-log/components/Line";
import StartEvent from "src/modules/activity-log/components/StartEvent";
import AwakeEvent from "src/modules/activity-log/components/AwakeEvent";
import SleepingEvent from "src/modules/activity-log/components/SleepingEvent";
import withEventAudioPlayer from "src/modules/activity-log/enhancers/withEventAudioPlayer";
import withStartTimeColumnWidth from "src/modules/activity-log/enhancers/withStartTimeColumnWidth";
import ScrollBox from "src/atoms/ScrollBox/index";
import CaptureEvent from "src/modules/activity-log/components/CaptureEvent";
import {
  eventsFilters,
  isMediaEvent
} from "src/modules/activity-log/supported-events";

const THIRTY_EIGHT_MIN_IN_MS = 2280000;

function filterEvent(event) {
  return eventsFilters[event.eventType]?.(event) || false;
}

function swapArrayElements(array, item1, item2) {
  const item = array[item1];
  array[item1] = array[item2];
  array[item2] = item;
}

function sortEvents(events) {
  const sortedEvents = events;
  events.forEach((event, index) => {
    if (event.id === sortedEvents[index - 1]?.referenceId?.value) {
      swapArrayElements(sortedEvents, index, index - 1);
    }
  });
  return sortedEvents;
}

function getLineHeight(eventType, nextEventType, isParentEvent) {
  if (isParentEvent) {
    return "50px";
  }
  if (isMediaEvent(eventType) && isMediaEvent(nextEventType)) {
    return "100px";
  }
  if (isMediaEvent(eventType) || isMediaEvent(nextEventType)) {
    return "62px";
  }
  return "24px";
}

class LiveActivityLog extends PureComponent {
  componentDidMount() {
    this.intervalId = setInterval(() => this.forceUpdate(), 10000);
  }
  componentWillUnmount() {
    clearInterval(this.intervalId);
    this.props.eventAudioPlayer.stop();
  }

  getFormattedEventStartTime(eventStart) {
    const { t, session } = this.props;
    const monitoringStartDate = time(session.startDate);
    const startTime =
      time().asMilliseconds() -
      (time(monitoringStartDate).asMilliseconds() + eventStart);
    return startTime < 60 * 1000
      ? t("activityLog.momentAgo.text")
      : t("someTimeAgo", {
          value0: time.duration(startTime).format("h_ m_")
        });
  }

  render() {
    const {
      session,
      avatar,
      eventAudioPlayer,
      startTimeColumnWidth,
      openEventView,
      toggleFreemiumFlow
    } = this.props;
    const { events, freemium } = session;
    const lineMarginLeft = `${startTimeColumnWidth + 25}px`;
    const startDate = session.startDate;
    const filteredEvents = Object.values(events).filter(filterEvent);
    filteredEvents
      .sort((a, b) => (a.start > b.start ? 1 : b.start > a.start ? -1 : 0))
      .reverse();
    const sortedEvents = sortEvents(filteredEvents);

    return (
      <>
        <Box py="16px" pl="14px" pr="1px" height="100%">
          <ScrollBox height="100%" pr="15px">
            <MonitoringInfo startDate={startDate} avatar={avatar} />
            <Line
              ml={lineMarginLeft}
              height={getLineHeight(null, sortedEvents[0]?.eventType)}
            />
            {sortedEvents.map((event, index) => {
              if (
                index === events.length - 1 &&
                (event.eventType === "AWAKE" || event.eventType === "SLEEPING")
              ) {
                return null;
              }
              const commonProps = {
                event,
                startTimeColumnWidth,
                avatar,
                activityLogType: "live",
                formattedStartTime: this.getFormattedEventStartTime(event.start)
              };

              if (
                event.referenceId?.value &&
                event.referenceId.value === sortedEvents[index - 1]?.id
              ) {
                commonProps.hasParent = true;
              }

              if (event.id === sortedEvents[index + 1]?.referenceId?.value) {
                commonProps.isParent = true;
              }

              return (
                <React.Fragment key={event.id}>
                  {event.eventType === "AWAKE" && (
                    <AwakeEvent
                      toggleFreemiumFlow={toggleFreemiumFlow}
                      eventAudioPlayer={eventAudioPlayer}
                      freemium={freemium}
                      {...commonProps}
                    />
                  )}
                  {event.eventType === "SLEEPING" && (
                    <SleepingEvent {...commonProps} />
                  )}
                  {isMediaEvent(event.eventType) && (
                    <CaptureEvent
                      {...commonProps}
                      openEventView={() => {
                        eventAudioPlayer.stop();
                        openEventView(event.id);
                      }}
                      isLive
                    />
                  )}
                  <Line
                    height={getLineHeight(
                      event.eventType,
                      sortedEvents[index + 1]?.eventType,
                      commonProps.isParent
                    )}
                    ml={lineMarginLeft}
                  />
                </React.Fragment>
              );
            })}
            <Box py="4px">
              <StartEvent
                startTimeColumnWidth={startTimeColumnWidth}
                startDate={startDate}
                activityLogType="live"
              />
            </Box>
          </ScrollBox>
        </Box>
      </>
    );
  }
}

const widestEventStartTime = time.duration(-THIRTY_EIGHT_MIN_IN_MS).humanize();

const enhance = compose(
  withTranslation(),
  withStartTimeColumnWidth({
    startTime: widestEventStartTime,
    maxWidth: 60
  }),
  withEventAudioPlayer
);

export default enhance(LiveActivityLog);
